/**
 * 统一的 Token 管理工具
 * 支持 access token、refresh token 和过期时间管理
 */

const ACCESS_TOKEN_KEY = 'plantai.access_token';
const REFRESH_TOKEN_KEY = 'plantai.refresh_token';
const EXPIRES_AT_KEY = 'plantai.expires_at';

/**
 * 获取访问令牌
 * @returns {string|null} 访问令牌或null
 */
export const getToken = () => {
    try {
        const token = localStorage.getItem(ACCESS_TOKEN_KEY);
        return token && token !== 'undefined' && token !== 'null' && token.trim() !== '' ? token : null;
    } catch (error) {
        console.error('获取访问令牌失败:', error);
        return null;
    }
};

/**
 * 设置访问令牌
 * @param {string} token - 访问令牌
 * @param {number} expiresInSec - 过期时间（秒），可选
 */
export const setToken = (token, expiresInSec) => {
    try {
        if (token && token !== 'undefined' && token !== 'null' && token.trim() !== '') {
            localStorage.setItem(ACCESS_TOKEN_KEY, token);

            // 设置过期时间
            if (expiresInSec) {
                const expiresAt = Date.now() + (expiresInSec * 1000);
                localStorage.setItem(EXPIRES_AT_KEY, expiresAt.toString());
            }

            return true;
        } else {
            console.warn('无效的访问令牌，未存储');
            return false;
        }
    } catch (error) {
        console.error('存储访问令牌失败:', error);
        return false;
    }
};

/**
 * 清除访问令牌
 */
export const clearToken = () => {
    try {
        localStorage.removeItem(ACCESS_TOKEN_KEY);
        localStorage.removeItem(EXPIRES_AT_KEY);
    } catch (error) {
        console.error('清除访问令牌失败:', error);
    }
};

/**
 * 获取刷新令牌
 * @returns {string|null} 刷新令牌或null
 */
export const getRefreshToken = () => {
    try {
        const token = localStorage.getItem(REFRESH_TOKEN_KEY);
        return token && token !== 'undefined' && token !== 'null' && token.trim() !== '' ? token : null;
    } catch (error) {
        console.error('获取刷新令牌失败:', error);
        return null;
    }
};

/**
 * 设置刷新令牌
 * @param {string} token - 刷新令牌
 */
export const setRefreshToken = (token) => {
    try {
        if (token && token !== 'undefined' && token !== 'null' && token.trim() !== '') {
            localStorage.setItem(REFRESH_TOKEN_KEY, token);
            return true;
        } else {
            console.warn('无效的刷新令牌，未存储');
            return false;
        }
    } catch (error) {
        console.error('存储刷新令牌失败:', error);
        return false;
    }
};

/**
 * 清除刷新令牌
 */
export const clearRefreshToken = () => {
    try {
        localStorage.removeItem(REFRESH_TOKEN_KEY);
    } catch (error) {
        console.error('清除刷新令牌失败:', error);
    }
};

/**
 * 解析本地过期时间戳（优先 plantai.expires_at，其次 JWT exp）
 * @returns {number|null} 过期毫秒时间戳
 */
export const getTokenExpiresAt = () => {
    try {
        const expiresAt = localStorage.getItem(EXPIRES_AT_KEY);
        if (expiresAt) {
            const n = parseInt(expiresAt, 10);
            if (!Number.isNaN(n) && n > 0) return n;
        }
        const payload = parseJwtPayload(getToken());
        if (payload?.exp) {
            const n = Number(payload.exp) * 1000;
            if (!Number.isNaN(n) && n > 0) return n;
        }
    } catch (error) {
        console.error('读取令牌过期时间失败:', error);
    }
    return null;
};

/**
 * 检查访问令牌是否过期
 * @returns {boolean} 是否过期
 */
export const isTokenExpired = () => {
    try {
        const expiresTime = getTokenExpiresAt();
        if (!expiresTime) {
            return false;
        }
        // 提前5分钟认为过期，避免临界时间问题
        return Date.now() >= (expiresTime - 5 * 60 * 1000);
    } catch (error) {
        console.error('检查令牌过期时间失败:', error);
        return false;
    }
};

/**
 * 清除所有令牌
 */
export const clearAllTokens = () => {
    clearToken();
    clearRefreshToken();
};

/**
 * 检查用户是否已登录（基于访问令牌存在性和有效性）
 * @returns {boolean} 是否已登录
 */
export const isLoggedIn = () => {
    const token = getToken();
    return token && !isTokenExpired();
};

/**
 * 解析 JWT payload（不校验签名，仅用于前端读取 tenantId 等声明）
 */
export const parseJwtPayload = (token) => {
    if (!token) {
        return null;
    }
    const parts = token.split('.');
    if (parts.length !== 3) {
        return null;
    }
    try {
        const base64 = parts[1].replace(/-/g, '+').replace(/_/g, '/');
        const padded = base64 + '='.repeat((4 - (base64.length % 4)) % 4);
        const binary = atob(padded);
        const bytes = Uint8Array.from(binary, (c) => c.charCodeAt(0));
        const json = new TextDecoder('utf-8').decode(bytes);
        return JSON.parse(json);
    } catch {
        return null;
    }
};

/**
 * 从 JWT 读取 tenantId
 */
export const getTenantIdFromToken = () => {
    const payload = parseJwtPayload(getToken());
    const tid = payload?.tenantId || payload?.tenant_id;
    if (tid && String(tid).trim() && String(tid).trim() !== 'undefined') {
        return String(tid).trim();
    }
    return null;
};

/**
 * 获取当前租户 ID（与小程序 getTenantId 对齐：JWT > localStorage > user 对象）
 * @param {string} fallback
 * @returns {string}
 */
export const getTenantId = (fallback = 'default') => {
    try {
        const jwtTid = getTenantIdFromToken();
        if (jwtTid) {
            localStorage.setItem('tenantId', jwtTid);
            return jwtTid;
        }

        let userTid = null;
        const userRaw = localStorage.getItem('user');
        if (userRaw) {
            const u = JSON.parse(userRaw);
            const tid = u?.tenantId || u?.tenant_id || u?.tenantCode || u?.tenant_code;
            if (tid && String(tid).trim() && String(tid).trim() !== 'undefined' && String(tid).trim() !== 'null') {
                userTid = String(tid).trim();
            }
        }
        const fromKey = localStorage.getItem('tenantId');
        const keyTid =
            fromKey && fromKey.trim() && fromKey !== 'undefined' && fromKey !== 'null'
                ? fromKey.trim()
                : null;
        // 登录用户 tenantId 优先于 standalone 缓存，避免换租户后仍读旧值
        if (userTid) {
            if (keyTid !== userTid) {
                localStorage.setItem('tenantId', userTid);
            }
            return userTid;
        }
        if (keyTid) return keyTid;
    } catch (error) {
        console.warn('读取 tenantId 失败:', error);
    }
    return fallback;
};

/**
 * 设置用户信息和令牌（兼容旧接口）
 * @param {Object} userData - 用户数据
 * @param {string} accessToken - 访问令牌
 * @param {string} refreshToken - 刷新令牌，可选
 * @param {number} expiresInSec - 过期时间（秒），可选
 */
export const setUserAndToken = (userData, accessToken, refreshToken, expiresInSec) => {
    try {
        // 设置用户信息
        localStorage.setItem('user', JSON.stringify(userData));

        const tid = userData?.tenantId || userData?.tenant_id;
        if (tid && String(tid).trim()) {
            localStorage.setItem('tenantId', String(tid).trim());
        }

        // 设置访问令牌
        const tokenSet = setToken(accessToken, expiresInSec);

        // 设置刷新令牌（如果提供）
        let refreshSet = true;
        if (refreshToken) {
            refreshSet = setRefreshToken(refreshToken);
        }

        if (tokenSet && refreshSet) {
            return true;
        } else {
            console.error('存储用户信息和令牌失败');
            return false;
        }
    } catch (error) {
        console.error('存储用户信息和令牌失败:', error);
        return false;
    }
};

/**
 * 清除用户信息和所有令牌
 */
export const clearUserAndToken = () => {
    try {
        localStorage.removeItem('user');
        localStorage.removeItem('tenantId');
        localStorage.removeItem('permissions');
        localStorage.removeItem('authContext');
        clearAllTokens();
    } catch (error) {
        console.error('清除用户信息和令牌失败:', error);
    }
};

/**
 * 验证令牌是否有效
 * @param {string} token - 要验证的令牌
 * @returns {boolean} 令牌是否有效
 */
export const isValidToken = (token) => {
    if (!token ||
        token === 'undefined' ||
        token === 'null' ||
        token.trim() === '') {
        return false;
    }
    if (token.startsWith('mock_token_')
        || token.startsWith('miniprogram_token_')
        || token.startsWith('local_token_')
        || token.startsWith('sms_token_')) {
        return false;
    }
    return token.split('.').length === 3;
};

// 启动时从 user 同步 tenantId，避免 PC 端长期停留在 default 而小程序已用登录租户
try {
    if (typeof localStorage !== 'undefined') {
        getTenantId();
    }
} catch {
    /* ignore */
}
